import i18n from "i18next";

import type {
  IOverlayElement,
  IOverlayElementText,
  IStudioOverlay,
} from "../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Overlay.interface";

/**
 * Localización de los textos de las escenas (scene templates). El copy fuente
 * de los seeds es español (`options.text`); el backend adjunta
 * `options.i18n = { en, pt }` por elemento de texto (ver `scene-i18n.ts` del
 * backend). Acá elegimos el idioma activo de la UI y caemos al español si una
 * traducción falta (símbolos / números / chrome ya en inglés no traen `i18n` y
 * rinden idénticos en los tres idiomas).
 *
 * Se aplica tanto al preview del picker (render vivo de fallback) como a la
 * escena que se materializa al aplicar (`buildStudioFromSceneTemplate`), y los
 * thumbnails estáticos viven por idioma en Garage
 * (`scene-thumbnails/<lang>/<slug>.webp`).
 */
export type SceneLang = "en" | "es" | "pt";

/** Idioma activo de la UI, normalizado a en/es/pt (default en). */
export const currentSceneLang = (): SceneLang => {
  const code = (i18n.language || "en").split("-")[0];
  return code === "es" || code === "pt" ? code : "en";
};

/** Texto de un elemento en el idioma pedido, con el español como fallback. */
export const resolveSceneText = (
  options: IOverlayElementText["options"],
  lang: SceneLang = currentSceneLang(),
): string => {
  if (lang === "es") return options.text;
  return options.i18n?.[lang] ?? options.text;
};

/**
 * Devuelve un overlay con el `text` de cada elemento de texto ya resuelto al
 * idioma pedido (no muta el original). Usado por el render vivo del picker.
 */
export const localizeOverlay = (
  overlay: IStudioOverlay,
  lang: SceneLang = currentSceneLang(),
): IStudioOverlay => {
  const options = overlay.options as
    | { elements?: IOverlayElement[] }
    | undefined;
  const elements = options?.elements;
  if (!elements?.length) return overlay;
  return {
    ...overlay,
    options: {
      ...(overlay.options as Record<string, unknown>),
      elements: elements.map((el) =>
        el.kind === "text"
          ? {
              ...el,
              options: {
                ...el.options,
                text: resolveSceneText(el.options, lang),
              },
            }
          : el,
      ),
    },
  } as IStudioOverlay;
};

/**
 * Reescribe la URL del thumbnail estático al idioma activo. El backend siembra
 * `…/scene-thumbnails/es/<slug>.webp`; acá cambiamos el segmento de idioma.
 */
export const localizedThumbnailUrl = (
  url: string | undefined,
  lang: SceneLang = currentSceneLang(),
): string | undefined => {
  if (!url) return url;
  return url.replace("/scene-thumbnails/es/", `/scene-thumbnails/${lang}/`);
};
